import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Search,
  Plus,
  RefreshCw,
  Loader2,
  MoreHorizontal,
  Mail,
  Pause,
  Play,
  Trash2,
  TrendingUp,
  UserCheck,
  ArrowUpRight,
  Landmark,
  Home,
  Building,
  Shield,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  Paperclip,
  MapPin,
  DollarSign,
  Target,
  MessageSquare,
} from "lucide-react";
import {
  fetchPartnersDashboard,
  pausePartner,
  resumePartner,
  deletePartner,
  fetchRealtorEmailHistory,
  fetchInsuranceEmailHistory,
} from "../api/partners";
import AddPartnerModal from "./AddPartnerModal";
import PartnerDetailPanel from "./PartnerDetailPanel";

// Partner type configuration
const PARTNER_TYPE_CONFIG = {
  lending: {
    label: "Lending",
    icon: Landmark,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    receivesEmails: true,
  },
  realtor: {
    label: "Realtor",
    icon: Home,
    color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    receivesEmails: false,
  },
  property_manager: {
    label: "Property Mgr",
    icon: Building,
    color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    receivesEmails: false,
  },
  insurance_agent: {
    label: "Insurance",
    icon: Shield,
    color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    receivesEmails: false,
  },
};

// Filter options
const FILTER_OPTIONS = [
  { value: "", label: "All Partners" },
  { value: "lending", label: "Lending" },
  { value: "realtor", label: "Realtor" },
  { value: "property_manager", label: "Property Mgr" },
  { value: "insurance_agent", label: "Insurance" },
];

// Partner Type Badge Component
const PartnerTypeBadge = ({ type }) => {
  const config = PARTNER_TYPE_CONFIG[type] || PARTNER_TYPE_CONFIG.lending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subValue, trend, color = "sky" }) => {
  const colorClasses = {
    sky: "text-sky-600 dark:text-sky-400 bg-sky-100 dark:bg-sky-900/30",
    emerald: "text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30",
    amber: "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30",
    violet: "text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/30",
  };

  return (
    <div className="flex-1 min-w-[160px] p-4 rounded-xl border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate">
            {label}
          </p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {value}
            </p>
            {trend !== undefined && (
              <span className={`text-xs font-medium ${trend >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {trend >= 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
          {subValue && (
            <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
              {subValue}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const isActive = status === "active";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isActive
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
          : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
      }`}
    >
      {isActive ? "Active" : "Paused"}
    </span>
  );
};

// Partner Row Component
// Email History Card Component
const EmailHistoryCard = ({ email }) => {
  const sentDate = new Date(email.sent_at);
  const formattedDate = sentDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const formattedTime = sentDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Format price range to abbreviated form
  const formatPrice = (price) => {
    if (!price) return null;
    // Try to extract numbers and format
    const match = price.match(/\$?([\d,]+)/g);
    if (match && match.length >= 1) {
      const values = match.map(v => {
        const num = parseInt(v.replace(/[$,]/g, ''));
        if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `$${Math.round(num / 1000)}K`;
        return `$${num}`;
      });
      return values.join('-');
    }
    return price;
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
      {/* Header row with date and status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <Clock className="w-3.5 h-3.5" />
          <span>{formattedDate} {formattedTime}</span>
        </div>
        {email.status === 'sent' ? (
          <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircle className="w-3.5 h-3.5" />
            Sent
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
            <XCircle className="w-3.5 h-3.5" />
            Failed
          </span>
        )}
      </div>

      {/* Buyer info */}
      <div className="mb-2">
        <span className="text-sm text-slate-600 dark:text-slate-300">Buyer: </span>
        <span className="text-sm font-medium text-slate-900 dark:text-white">{email.buyer_name}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">({email.buyer_email})</span>
      </div>

      {/* Property details */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        {email.property_type && (
          <span className="flex items-center gap-1">
            <Home className="w-3 h-3" />
            {email.property_type}
          </span>
        )}
        {email.property_location && (
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {email.property_location}
          </span>
        )}
        {email.price_range && (
          <span className="flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            {formatPrice(email.price_range)}
          </span>
        )}
      </div>

      {/* Purpose and timeline */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mt-1">
        {email.loan_purpose && (
          <span className="flex items-center gap-1">
            <Target className="w-3 h-3" />
            {email.loan_purpose}
          </span>
        )}
        {email.timeline && (
          <span>Timeline: {email.timeline}</span>
        )}
      </div>

      {/* Referral agreement indicator */}
      {email.include_referral_agreement && (
        <div className="flex items-center gap-1 mt-2 text-xs text-purple-600 dark:text-purple-400">
          <Paperclip className="w-3 h-3" />
          Referral Agreement Attached
        </div>
      )}

      {/* Error message if failed */}
      {email.status === 'failed' && email.error_message && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
          Error: {email.error_message}
        </div>
      )}
    </div>
  );
};

const PartnerRow = ({ partner, onSelect, onPause, onResume, onDelete, isLoading, isExpanded, onToggleExpand, emailHistory, emailHistoryLoading }) => {
  const [showActions, setShowActions] = useState(false);

  const handleAction = (e, action) => {
    e.stopPropagation();
    setShowActions(false);
    action();
  };

  const conversionRate = partner.leadsAccessed > 0
    ? ((partner.leadsConverted / partner.leadsAccessed) * 100).toFixed(1)
    : 0;

  const partnerType = partner.partner_type || "lending";
  const typeConfig = PARTNER_TYPE_CONFIG[partnerType] || PARTNER_TYPE_CONFIG.lending;
  const isRealtor = partnerType === "realtor";
  const isInsuranceAgent = partnerType === "insurance_agent";
  const hasEmailHistory = isRealtor || isInsuranceAgent;
  const emailCount = emailHistory?.total ?? partner.emailsSent ?? 0;

  const handleRowClick = (e) => {
    // For realtors and insurance agents, toggle expansion. For others, open detail panel.
    if (hasEmailHistory) {
      onToggleExpand(partner.email, partnerType);
    } else {
      onSelect(partner);
    }
  };

  return (
    <>
      <tr
        onClick={handleRowClick}
        className={`group cursor-pointer transition-colors ${
          isExpanded
            ? "bg-slate-100 dark:bg-slate-800"
            : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
        }`}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Expand indicator for realtors and insurance agents */}
            {hasEmailHistory ? (
              <ChevronRight
                className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${
                  isExpanded ? "rotate-90" : ""
                }`}
              />
            ) : (
              <div className="w-4" />
            )}
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
              {partner.name?.charAt(0)?.toUpperCase() || partner.email?.charAt(0)?.toUpperCase() || "P"}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-slate-900 dark:text-white truncate">
                  {partner.name || partner.email}
                </p>
                <PartnerTypeBadge type={partnerType} />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {partner.email}
              </p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-slate-600 dark:text-slate-300">
            {partner.company || "—"}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            {partner.leadsAccessed || 0}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
            {partner.leadsActive || 0}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            {partner.leadsConverted || 0}
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
            ({conversionRate}%)
          </span>
        </td>
        <td className="px-4 py-3">
          {/* For realtors and insurance agents, show email count. For lending, show notification status */}
          {hasEmailHistory ? (
            <span className={`text-sm font-medium ${emailCount > 0 ? (isInsuranceAgent ? 'text-orange-600 dark:text-orange-400' : 'text-purple-600 dark:text-purple-400') : 'text-slate-400'}`}>
              {emailHistoryLoading ? (
                <Loader2 className="w-4 h-4 animate-spin inline" />
              ) : (
                emailCount
              )}
            </span>
          ) : typeConfig.receivesEmails ? (
            partner.receives_notifications !== false ? (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                Receiving
              </span>
            ) : (
              <span className="text-xs text-slate-400">Paused</span>
            )
          ) : (
            <span className="text-xs text-slate-400 italic">N/A</span>
          )}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={partner.status} />
        </td>
        <td className="px-4 py-3">
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
              disabled={isLoading}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MoreHorizontal className="w-4 h-4" />
              )}
            </button>

            <AnimatePresence>
              {showActions && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(false);
                    }}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -5 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -5 }}
                    className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-20 overflow-hidden"
                  >
                    <button
                      onClick={(e) => handleAction(e, () => window.open(`mailto:${partner.email}?cc=partners@nhamy.us`))}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      <Mail className="w-4 h-4" />
                      Send Email
                    </button>
                    {/* View Details button */}
                    <button
                      onClick={(e) => handleAction(e, () => onSelect(partner))}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      <UserCheck className="w-4 h-4" />
                      View Details
                    </button>
                    {/* Only show pause/resume for lending partners */}
                    {typeConfig.receivesEmails && (
                      <>
                        {partner.status === "active" ? (
                          <button
                            onClick={(e) => handleAction(e, () => onPause(partner.email))}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            <Pause className="w-4 h-4" />
                            Pause Notifications
                          </button>
                        ) : (
                          <button
                            onClick={(e) => handleAction(e, () => onResume(partner.email))}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            <Play className="w-4 h-4" />
                            Resume Notifications
                          </button>
                        )}
                      </>
                    )}
                    <button
                      onClick={(e) => handleAction(e, () => onDelete(partner.email))}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove Partner
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </td>
      </tr>

      {/* Email History Expansion Row - For realtors and insurance agents */}
      <AnimatePresence>
        {isExpanded && hasEmailHistory && (
          <tr>
            <td colSpan={8} className="px-0 py-0">
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-4 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-b border-slate-200 dark:border-slate-700">
                  <div className="ml-7">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className={`w-4 h-4 ${isInsuranceAgent ? 'text-orange-500' : 'text-purple-500'}`} />
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        Email History
                      </span>
                      {emailHistory && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          ({emailHistory.total} {emailHistory.total === 1 ? 'email' : 'emails'})
                        </span>
                      )}
                    </div>

                    {emailHistoryLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className={`w-6 h-6 ${isInsuranceAgent ? 'text-orange-500' : 'text-purple-500'} animate-spin`} />
                        <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">
                          Loading email history...
                        </span>
                      </div>
                    ) : emailHistory?.emails?.length > 0 ? (
                      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                        {emailHistory.emails.map((email) => (
                          <EmailHistoryCard key={email.email_id} email={email} />
                        ))}
                        {emailHistory.emails.length >= 50 && (
                          <p className="text-xs text-slate-400 text-center py-2">
                            Showing most recent 50 emails
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <MessageSquare className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          No leads sent to this {isInsuranceAgent ? 'insurance agent' : 'realtor'} yet.
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                          Use "Send to {isInsuranceAgent ? 'Insurance' : 'Realtor'}" from the Leads page to share leads.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
};

// Empty State Component
const EmptyState = ({ onAddPartner, filterType }) => {
  const typeLabel = filterType ? PARTNER_TYPE_CONFIG[filterType]?.label?.toLowerCase() : null;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
        <Users className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
        {filterType ? `No ${typeLabel} partners yet` : "No partners yet"}
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-sm mb-6">
        {filterType === "realtor"
          ? "Add realtor partners to send them leads. Lead notifications coming soon!"
          : filterType === "property_manager"
          ? "Add property manager partners. Lead notifications coming soon!"
          : filterType === "insurance_agent"
          ? "Add insurance agent partners to give them access to leads and send them notifications."
          : "Add referral partners to give them access to leads and track their conversion performance."}
      </p>
      <button
        onClick={onAddPartner}
        className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Your First Partner
      </button>
    </div>
  );
};

// Main Dashboard Component
const PartnersDashboard = ({ userEmail, onNavigateToCase }) => {
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalLeadsAccessed: 0,
    conversionRate: 0,
    partnersByType: { lending: 0, realtor: 0, property_manager: 0, insurance_agent: 0 },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState("name"); // name, company, accessed, active, converted, status
  const [sortDirection, setSortDirection] = useState("asc"); // asc or desc

  // Modal & Panel States
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  // Email History Expansion State (for realtor and insurance agent partners)
  const [expandedPartner, setExpandedPartner] = useState(null); // email of expanded partner
  const [emailHistoryData, setEmailHistoryData] = useState({}); // { [email]: { emails: [], total: 0 } }
  const [emailHistoryLoading, setEmailHistoryLoading] = useState({}); // { [email]: boolean }

  // Handle toggling partner row expansion (for realtors and insurance agents)
  const handleToggleExpand = useCallback(async (partnerEmail, partnerType) => {
    if (expandedPartner === partnerEmail) {
      // Collapse if already expanded
      setExpandedPartner(null);
      return;
    }

    // Expand and fetch email history
    setExpandedPartner(partnerEmail);

    // If we haven't loaded this partner's email history yet, fetch it
    if (!emailHistoryData[partnerEmail]) {
      setEmailHistoryLoading(prev => ({ ...prev, [partnerEmail]: true }));
      try {
        // Use appropriate fetch function based on partner type
        const fetchFn = partnerType === "insurance_agent"
          ? fetchInsuranceEmailHistory
          : fetchRealtorEmailHistory;
        const response = await fetchFn(partnerEmail, { limit: 50 });
        setEmailHistoryData(prev => ({
          ...prev,
          [partnerEmail]: {
            emails: response.emails || [],
            total: response.total || 0,
          }
        }));
      } catch (err) {
        console.error("Failed to fetch email history:", err);
        setEmailHistoryData(prev => ({
          ...prev,
          [partnerEmail]: { emails: [], total: 0 }
        }));
      } finally {
        setEmailHistoryLoading(prev => ({ ...prev, [partnerEmail]: false }));
      }
    }
  }, [expandedPartner, emailHistoryData]);

  // Fetch dashboard data
  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    setError(null);

    try {
      const response = await fetchPartnersDashboard();
      const data = response?.data || response;

      // Map API response to frontend format
      const partnersData = (data.by_partner || data.partners || []).map(p => ({
        ...p,
        leadsAccessed: p.accessed ?? p.leadsAccessed ?? p.stats?.leads_accessed ?? 0,
        leadsConverted: p.converted ?? p.leadsConverted ?? p.stats?.leads_converted ?? 0,
        leadsActive: p.active ?? p.leadsActive ?? p.stats?.leads_active ?? 0,
        leadsLost: p.lost ?? p.leadsLost ?? p.stats?.leads_lost ?? 0,
        lastActive: p.last_active ?? p.lastActive ?? p.stats?.last_active,
        receivesNotifications: p.receives_notifications ?? p.receivesNotifications ?? true,
        partner_type: p.partner_type || "lending",
      }));

      setPartners(partnersData);
      setStats({
        total: data.total_partners ?? data.stats?.total ?? partnersData.length ?? 0,
        active: data.active_partners ?? data.stats?.active ?? partnersData.filter(p => p.status === 'active').length ?? 0,
        totalLeadsAccessed: data.total_leads_accessed ?? data.stats?.totalLeadsAccessed ?? 0,
        totalLeadsConverted: data.total_leads_converted ?? 0,
        conversionRate: data.conversion_rate ?? data.stats?.conversionRate ?? 0,
        partnersByType: data.partners_by_type ?? {
          lending: partnersData.filter(p => p.partner_type === 'lending').length,
          realtor: partnersData.filter(p => p.partner_type === 'realtor').length,
          property_manager: partnersData.filter(p => p.partner_type === 'property_manager').length,
          insurance_agent: partnersData.filter(p => p.partner_type === 'insurance_agent').length,
        },
      });
    } catch (err) {
      console.error("Failed to fetch partners:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle pause partner
  const handlePause = async (email) => {
    setActionLoading(email);
    try {
      await pausePartner(email);
      setPartners(prev =>
        prev.map(p => (p.email === email ? { ...p, status: "paused" } : p))
      );
      setStats(prev => ({
        ...prev,
        active: Math.max(0, prev.active - 1),
      }));
    } catch (err) {
      console.error("Failed to pause partner:", err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle resume partner
  const handleResume = async (email) => {
    setActionLoading(email);
    try {
      await resumePartner(email);
      setPartners(prev =>
        prev.map(p => (p.email === email ? { ...p, status: "active" } : p))
      );
      setStats(prev => ({
        ...prev,
        active: prev.active + 1,
      }));
    } catch (err) {
      console.error("Failed to resume partner:", err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle delete partner
  const handleDelete = async (email) => {
    if (!window.confirm("Are you sure you want to remove this partner?")) return;

    setActionLoading(email);
    try {
      await deletePartner(email);
      const partner = partners.find(p => p.email === email);
      setPartners(prev => prev.filter(p => p.email !== email));
      setStats(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        active: partner?.status === "active" ? Math.max(0, prev.active - 1) : prev.active,
      }));
    } catch (err) {
      console.error("Failed to delete partner:", err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle partner added
  const handlePartnerAdded = (newPartner) => {
    setPartners(prev => [newPartner, ...prev]);
    const partnerType = newPartner.partner_type || "lending";
    setStats(prev => ({
      ...prev,
      total: prev.total + 1,
      active: newPartner.status === "active" ? prev.active + 1 : prev.active,
      partnersByType: {
        ...prev.partnersByType,
        [partnerType]: (prev.partnersByType[partnerType] || 0) + 1,
      },
    }));
    setIsAddModalOpen(false);
  };

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      // New field, default to descending for numeric fields, ascending for text
      setSortField(field);
      setSortDirection(["accessed", "active", "converted"].includes(field) ? "desc" : "asc");
    }
  };

  // Filter and sort partners
  const filteredPartners = partners
    .filter(partner => {
      const search = searchTerm.toLowerCase();
      const matchesSearch = (
        partner.email?.toLowerCase().includes(search) ||
        partner.name?.toLowerCase().includes(search) ||
        partner.company?.toLowerCase().includes(search)
      );
      const matchesType = !typeFilter || partner.partner_type === typeFilter;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case "name":
          aVal = (a.name || a.email || "").toLowerCase();
          bVal = (b.name || b.email || "").toLowerCase();
          break;
        case "company":
          aVal = (a.company || "").toLowerCase();
          bVal = (b.company || "").toLowerCase();
          break;
        case "accessed":
          aVal = a.leadsAccessed || 0;
          bVal = b.leadsAccessed || 0;
          break;
        case "active":
          aVal = a.leadsActive || 0;
          bVal = b.leadsActive || 0;
          break;
        case "converted":
          aVal = a.leadsConverted || 0;
          bVal = b.leadsConverted || 0;
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading partners...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Partner Management
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage referral partners and track their lead access
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchData(true)}
            disabled={isRefreshing}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Partner</span>
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="flex flex-wrap gap-4">
        <StatsCard
          icon={Users}
          label="Total Partners"
          value={stats.total}
          color="sky"
        />
        <StatsCard
          icon={UserCheck}
          label="Active Partners"
          value={stats.active}
          subValue={`${stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% active`}
          color="emerald"
        />
        <StatsCard
          icon={ArrowUpRight}
          label="Leads Accessed"
          value={stats.totalLeadsAccessed}
          color="amber"
        />
        <StatsCard
          icon={TrendingUp}
          label="Leads Converted"
          value={stats.totalLeadsConverted || 0}
          subValue={`${stats.conversionRate}% rate`}
          color="violet"
        />
      </div>

      {/* Partners by Type Breakdown - Click to filter */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setTypeFilter(typeFilter === "lending" ? "" : "lending")}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
            typeFilter === "lending"
              ? "bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-500 ring-2 ring-blue-400/50"
              : "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600"
          }`}
        >
          <Landmark className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            {stats.partnersByType?.lending || 0} Lending
          </span>
        </button>
        <button
          onClick={() => setTypeFilter(typeFilter === "realtor" ? "" : "realtor")}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
            typeFilter === "realtor"
              ? "bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-500 ring-2 ring-green-400/50"
              : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:border-green-400 dark:hover:border-green-600"
          }`}
        >
          <Home className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span className="text-sm font-medium text-green-700 dark:text-green-300">
            {stats.partnersByType?.realtor || 0} Realtor
          </span>
        </button>
        <button
          onClick={() => setTypeFilter(typeFilter === "property_manager" ? "" : "property_manager")}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
            typeFilter === "property_manager"
              ? "bg-purple-100 dark:bg-purple-900/40 border-purple-400 dark:border-purple-500 ring-2 ring-purple-400/50"
              : "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 hover:border-purple-400 dark:hover:border-purple-600"
          }`}
        >
          <Building className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
            {stats.partnersByType?.property_manager || 0} Property Mgr
          </span>
        </button>
        <button
          onClick={() => setTypeFilter(typeFilter === "insurance_agent" ? "" : "insurance_agent")}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
            typeFilter === "insurance_agent"
              ? "bg-orange-100 dark:bg-orange-900/40 border-orange-400 dark:border-orange-500 ring-2 ring-orange-400/50"
              : "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 hover:border-orange-400 dark:hover:border-orange-600"
          }`}
        >
          <Shield className="w-4 h-4 text-orange-600 dark:text-orange-400" />
          <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
            {stats.partnersByType?.insurance_agent || 0} Insurance
          </span>
        </button>
      </div>

      {/* Filter Tabs & Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Type Filter Tabs */}
        <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setTypeFilter(option.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                typeFilter === option.value
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        {partners.length > 0 && (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search partners by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            />
          </div>
        )}
      </div>

      {/* Partners Table or Empty State */}
      {filteredPartners.length === 0 && !searchTerm ? (
        <EmptyState onAddPartner={() => setIsAddModalOpen(true)} filterType={typeFilter} />
      ) : (
        <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900/50 sticky top-0">
              <tr>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors select-none"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center gap-1">
                    Partner
                    {sortField === "name" ? (
                      sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronsUpDown className="w-3 h-3 opacity-40" />
                    )}
                  </div>
                </th>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors select-none"
                  onClick={() => handleSort("company")}
                >
                  <div className="flex items-center gap-1">
                    Company
                    {sortField === "company" ? (
                      sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronsUpDown className="w-3 h-3 opacity-40" />
                    )}
                  </div>
                </th>
                <th
                  className="text-center px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors select-none"
                  onClick={() => handleSort("accessed")}
                >
                  <div className="flex items-center justify-center gap-1">
                    Accessed
                    {sortField === "accessed" ? (
                      sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronsUpDown className="w-3 h-3 opacity-40" />
                    )}
                  </div>
                </th>
                <th
                  className="text-center px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors select-none"
                  onClick={() => handleSort("active")}
                >
                  <div className="flex items-center justify-center gap-1">
                    Active
                    {sortField === "active" ? (
                      sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronsUpDown className="w-3 h-3 opacity-40" />
                    )}
                  </div>
                </th>
                <th
                  className="text-center px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors select-none"
                  onClick={() => handleSort("converted")}
                >
                  <div className="flex items-center justify-center gap-1">
                    Converted
                    {sortField === "converted" ? (
                      sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronsUpDown className="w-3 h-3 opacity-40" />
                    )}
                  </div>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Emails
                </th>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors select-none"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === "status" ? (
                      sortDirection === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronsUpDown className="w-3 h-3 opacity-40" />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-12">
                  {/* Actions */}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredPartners.map((partner) => (
                <PartnerRow
                  key={partner.email}
                  partner={partner}
                  onSelect={setSelectedPartner}
                  onPause={handlePause}
                  onResume={handleResume}
                  onDelete={handleDelete}
                  isLoading={actionLoading === partner.email}
                  isExpanded={expandedPartner === partner.email}
                  onToggleExpand={handleToggleExpand}
                  emailHistory={emailHistoryData[partner.email]}
                  emailHistoryLoading={emailHistoryLoading[partner.email]}
                />
              ))}
            </tbody>
          </table>

          {filteredPartners.length === 0 && searchTerm && (
            <div className="py-12 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No partners found matching "{searchTerm}"
              </p>
            </div>
          )}
        </div>
      )}

      {/* Add Partner Modal */}
      <AddPartnerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onPartnerAdded={handlePartnerAdded}
      />

      {/* Partner Detail Panel */}
      <PartnerDetailPanel
        partner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
        onPause={handlePause}
        onResume={handleResume}
        onUpdate={(updated) => {
          setPartners(prev =>
            prev.map(p => (p.email === updated.email ? { ...p, ...updated } : p))
          );
          setSelectedPartner(null);
        }}
        onNavigateToCase={onNavigateToCase}
      />
    </div>
  );
};

export default PartnersDashboard;
